// Bezeichnungen des Einkaufs für Server und Oberfläche: Zustände, Bestellwege, Aufgabenarten.

export const ORDER_STATUS = {
  draft: 'draft',
  pending: 'pending_approval',
  returned: 'returned',
  approved: 'approved',
  ordered: 'ordered',
  partiallyReceived: 'partially_received',
  received: 'received',
  closed: 'closed',
  cancelled: 'cancelled',
} as const

export const ORDER_STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  pending_approval: 'Zur Freigabe',
  returned: 'Zurückgegeben',
  approved: 'Freigegeben',
  ordered: 'Bestellt',
  partially_received: 'Teilweise geliefert',
  received: 'Geliefert',
  closed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
  reversed: 'Storniert',
}

/** Bestellungen, auf deren Ware noch gewartet wird */
export const ORDER_OPEN_STATUSES = [ORDER_STATUS.ordered, ORDER_STATUS.partiallyReceived] as const
/** Bestellungen vor dem Bestellen – ihre Mengen zählen im Vorschlag als „in Vorbereitung“ */
export const ORDER_PREPARING_STATUSES = [ORDER_STATUS.draft, ORDER_STATUS.pending, ORDER_STATUS.returned, ORDER_STATUS.approved] as const

export const INVOICE_STATUS = { draft: 'draft', released: 'released', reversed: 'reversed' } as const

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: 'In Prüfung',
  released: 'Freigegeben',
  reversed: 'Storniert',
  cancelled: 'Abgebrochen',
}

/** Wie die Bestellung zum Lieferanten ging – das System selbst versendet nichts. */
export const SEND_CHANNELS = ['email', 'phone', 'portal', 'edi', 'fax', 'in_person'] as const
export type SendChannel = (typeof SEND_CHANNELS)[number]

export const SEND_CHANNEL_LABELS: Record<SendChannel, string> = {
  email: 'E-Mail',
  phone: 'Telefon',
  portal: 'Lieferantenportal',
  edi: 'EDI',
  fax: 'Fax',
  in_person: 'persönlich',
}

export const sendChannelLabel = (value: unknown): string | null => (typeof value === 'string' && value in SEND_CHANNEL_LABELS ? SEND_CHANNEL_LABELS[value as SendChannel] : null)

export const isSendChannel = (value: unknown): value is SendChannel => typeof value === 'string' && (SEND_CHANNELS as readonly string[]).includes(value)

/** Kopf-Zusatzfelder einer Bestellung */
export const ORDER_FIELDS = { sentVia: 'sentVia', sentAt: 'sentAt', sentBy: 'sentBy' } as const

export const TASK_KINDS = {
  orderApprove: 'purchasing.order.approve',
  orderSend: 'purchasing.order.send',
  receiptDifference: 'purchasing.receipt.difference',
  invoiceCheck: 'purchasing.invoice.check',
  creditRequest: 'purchasing.invoice.credit',
  creditNoteCheck: 'purchasing.creditNote.check',
  requisition: 'purchasing.requisition',
} as const

export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const

/** „Di, Fr“, zusammenhängende Tage als Spanne („Mo–Fr“) – leer heißt: jeder Werktag */
export function weekdaysText(days: readonly number[]): string {
  if (days.length === 0) return 'Mo–Fr'
  const sorted = [...new Set(days)].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b)
  const parts: string[] = []
  for (let index = 0; index < sorted.length; ) {
    let end = index
    while (end + 1 < sorted.length && sorted[end + 1] === (sorted[end] as number) + 1) end += 1
    const from = WEEKDAY_SHORT[(sorted[index] as number) - 1] as string
    const to = WEEKDAY_SHORT[(sorted[end] as number) - 1] as string
    parts.push(end - index >= 2 ? `${from}–${to}` : end > index ? `${from}, ${to}` : from)
    index = end + 1
  }
  return parts.join(', ')
}

export const REQUISITION_STATUS_LABELS: Record<string, string> = { open: 'Offen', ordered: 'Bestellt', dismissed: 'Abgelehnt', accepted: 'Übernommen' }

/** Zusatzzeilen einer Eingangsrechnung bzw. Lieferantengutschrift (AP8a): Belegzeilen ohne Artikel. */
export const EXTRA_LINE_KINDS = ['fee', 'discount', 'deposit', 'deposit_return'] as const
export type ExtraLineKind = (typeof EXTRA_LINE_KINDS)[number]

export const EXTRA_LINE_KIND_LABELS: Record<ExtraLineKind, string> = {
  fee: 'Zuschlag',
  discount: 'Abzug',
  deposit: 'Pfand',
  deposit_return: 'Leergut-Gutschrift',
}

/** Zuschlag oder Abzug (mit Steuerschlüssel und Betrag) – im Gegensatz zu Pfand und Leergut (mit Pfandart) */
export const isChargeKind = (kind: string): kind is 'fee' | 'discount' => kind === 'fee' || kind === 'discount'

export const isExtraLineKind = (kind: string): kind is ExtraLineKind => (EXTRA_LINE_KINDS as readonly string[]).includes(kind)
